/**
 * ProgressiveHTMLTree - Visible HTML tree for sighted users with progressive disclosure.
 *
 * Renders molecular structure as semantic HTML with <details>/<summary> elements.
 * Summary shows short name; expanding reveals full description.
 *
 * This is a plain HTML component (not inside SVG foreignObject) intended for
 * display in a right-hand panel alongside the reaction scheme preview.
 *
 * Consumes a `TreeLabelMap` (the same shape produced by
 * `buildAltTextMapFromStructure` and consumed by `HTMLStructureTree`) so the
 * RSG panel, the in-SVG accessibility tree, and the demo all render from one
 * source of truth.
 */
import type {
  SVGStructure,
  MoleculeNode,
  FeatureNode,
  AtomLeaf,
  BondLeaf,
  LonePairLeaf,
  TreeLabel,
  TreeLabelMap,
} from '@alchemiesolns/svg-interactive';

export interface ProgressiveHTMLTreeProps {
  structure: SVGStructure;
  /** Map of element IDs to {name, description} entries. */
  treeLabelMap: TreeLabelMap;
  /** Optional label for the navigation landmark */
  ariaLabel?: string;
}

// Styles
const DESCRIPTION_STYLE: React.CSSProperties = {
  margin: '4px 0 8px 0',
  padding: '4px 8px',
  backgroundColor: 'rgba(59, 130, 246, 0.1)',
  borderLeft: '2px solid rgba(59, 130, 246, 0.5)',
  fontSize: '12px',
  fontStyle: 'italic',
  color: '#555',
};

const LIST_STYLE: React.CSSProperties = {
  listStyle: 'none',
  paddingLeft: '16px',
  margin: '4px 0',
};

const SUMMARY_STYLE: React.CSSProperties = {
  cursor: 'pointer',
};

/**
 * Look up the tree-label entry for an element id, falling back to the bare
 * structure label when the map has no entry.
 */
function getEntry(
  id: string,
  fallbackLabel: string,
  treeLabelMap: TreeLabelMap,
): TreeLabel {
  return treeLabelMap.get(id) ?? { name: fallbackLabel };
}

// Component for lone pair items
function LonePairItem({
  lonePair,
  treeLabelMap,
}: {
  lonePair: LonePairLeaf;
  treeLabelMap: TreeLabelMap;
}) {
  const lpId = `${lonePair.atomId}-lp${lonePair.index}`;
  const entry = getEntry(lpId, lonePair.label, treeLabelMap);
  return <li>{entry.name}</li>;
}

// Component for bond items
function BondItem({
  bond,
  treeLabelMap,
}: {
  bond: BondLeaf;
  treeLabelMap: TreeLabelMap;
}) {
  const entry = getEntry(bond.bondId, bond.label, treeLabelMap);
  const hasEndpoints = bond.endpointNodes.length > 0;

  if (!hasEndpoints) {
    return <li>{entry.name}</li>;
  }

  return (
    <li>
      <details>
        <summary style={SUMMARY_STYLE}>{entry.name}</summary>
        {entry.description && <p style={DESCRIPTION_STYLE}>{entry.description}</p>}
        <ul style={LIST_STYLE}>
          {bond.endpointNodes.map((ep) => (
            <li key={ep.id}>{ep.label}</li>
          ))}
        </ul>
      </details>
    </li>
  );
}

// Component for atom items
function AtomItem({
  atom,
  treeLabelMap,
}: {
  atom: AtomLeaf;
  treeLabelMap: TreeLabelMap;
}) {
  // The map is keyed by atom.atomId in buildAltTextMapFromStructure; fall back
  // to atom.id for entries set by older paths.
  const entry =
    treeLabelMap.get(atom.atomId) ??
    treeLabelMap.get(atom.id) ??
    { name: atom.label };

  const hasChildren =
    atom.hydrogenNodes.length > 0 ||
    atom.lonePairNodes.length > 0 ||
    atom.bondNodes.length > 0;

  if (!hasChildren && !entry.description) {
    return <li>{entry.name}</li>;
  }

  if (!hasChildren && entry.description) {
    return (
      <li>
        <details>
          <summary style={SUMMARY_STYLE}>{entry.name}</summary>
          <p style={DESCRIPTION_STYLE}>{entry.description}</p>
        </details>
      </li>
    );
  }

  return (
    <li>
      <details>
        <summary style={SUMMARY_STYLE}>{entry.name}</summary>
        {entry.description && <p style={DESCRIPTION_STYLE}>{entry.description}</p>}
        <ul style={LIST_STYLE}>
          {atom.hydrogenNodes.map((h) => (
            <AtomItem key={h.id} atom={h} treeLabelMap={treeLabelMap} />
          ))}
          {atom.lonePairNodes.map((lp) => (
            <LonePairItem key={lp.id} lonePair={lp} treeLabelMap={treeLabelMap} />
          ))}
          {atom.bondNodes.map((bond) => (
            <BondItem key={bond.id} bond={bond} treeLabelMap={treeLabelMap} />
          ))}
        </ul>
      </details>
    </li>
  );
}

// Component for feature items
function FeatureItem({
  feature,
  treeLabelMap,
}: {
  feature: FeatureNode;
  treeLabelMap: TreeLabelMap;
}) {
  const entry = getEntry(feature.id, feature.label, treeLabelMap);

  const renderChild = (childId: string) => {
    const atom = feature.atoms.find((a) => a.id === childId);
    if (atom) {
      return <AtomItem key={atom.id} atom={atom} treeLabelMap={treeLabelMap} />;
    }

    const childFeature = feature.children.find((f) => f.id === childId);
    if (childFeature) {
      return (
        <FeatureItem
          key={childFeature.id}
          feature={childFeature}
          treeLabelMap={treeLabelMap}
        />
      );
    }

    return null;
  };

  return (
    <li>
      <details>
        <summary style={SUMMARY_STYLE}>{entry.name}</summary>
        {entry.description && <p style={DESCRIPTION_STYLE}>{entry.description}</p>}
        <ul style={LIST_STYLE}>
          {feature.orderedChildIds && feature.orderedChildIds.length > 0 ? (
            feature.orderedChildIds.map(renderChild)
          ) : (
            <>
              {feature.atoms.map((atom) => (
                <AtomItem key={atom.id} atom={atom} treeLabelMap={treeLabelMap} />
              ))}
              {feature.children.map((child) => (
                <FeatureItem
                  key={child.id}
                  feature={child}
                  treeLabelMap={treeLabelMap}
                />
              ))}
            </>
          )}
        </ul>
      </details>
    </li>
  );
}

// Component for molecule items
function MoleculeItem({
  molecule,
  treeLabelMap,
}: {
  molecule: MoleculeNode;
  treeLabelMap: TreeLabelMap;
}) {
  const entry = getEntry(molecule.id, molecule.label, treeLabelMap);
  const hasUnclaimed = molecule.unclaimedAtoms.length > 0;

  return (
    <details>
      <summary style={{ ...SUMMARY_STYLE, fontWeight: 500 }}>
        {entry.name}
      </summary>
      {entry.description && <p style={DESCRIPTION_STYLE}>{entry.description}</p>}
      <ul style={LIST_STYLE}>
        {molecule.features.map((f) => (
          <FeatureItem key={f.id} feature={f} treeLabelMap={treeLabelMap} />
        ))}
        {hasUnclaimed && (
          <li>
            <details>
              <summary style={SUMMARY_STYLE}>Other atoms</summary>
              <ul style={LIST_STYLE}>
                {molecule.unclaimedAtoms.map((a) => (
                  <AtomItem key={a.id} atom={a} treeLabelMap={treeLabelMap} />
                ))}
              </ul>
            </details>
          </li>
        )}
      </ul>
    </details>
  );
}

/**
 * ProgressiveHTMLTree - Renders molecular structure as an expandable HTML tree.
 */
export function ProgressiveHTMLTree({
  structure,
  treeLabelMap,
  ariaLabel,
}: ProgressiveHTMLTreeProps) {
  return (
    <div
      role="navigation"
      aria-label={ariaLabel || structure.tree.label}
      style={{
        fontSize: '13px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        lineHeight: '1.4',
        color: '#333',
      }}
    >
      {structure.tree.description && (
        <p style={{ margin: '0 0 8px 0', fontStyle: 'italic', color: '#666' }}>
          {structure.tree.description}
        </p>
      )}
      {structure.tree.molecules.map((mol) => (
        <MoleculeItem key={mol.id} molecule={mol} treeLabelMap={treeLabelMap} />
      ))}
    </div>
  );
}
